import React, { useMemo, useState } from "react"
import PageHeader from "../../components/Ui/Page/PageHeader"
import { useQuery } from "@apollo/client"
import ALL_CUSTOM_STATES from "../../graphql/queries/settings/customState/AllCustomStates"
import ALL_STORE_GROUPS from "../../graphql/queries/store/AllStoreGroups"
import ErrorMessage from "../../components/Ui/Messages/ErrorMessage"
import { NoticeBanner } from "../../components/Ui/Messages/NoticeBanner"
import InsightsCheckoutTable from "../../components/Insights/InsightsCheckoutTable"
import InsightsFilters from "../../components/Insights/InsightsFilters"
import { CustomState, StoreGroup } from "lib/types/generated/graphql-types"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"
import { useConfig } from "../../configuration/useConfig"
import { ReactComponent as RefreshIcon } from "images/icons/rotate-right.svg"
import { DashboardBarChart } from "../../components/DashboardSales/DashboardBarChart"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"
import { getCountryName } from "helpers/countries"
import { useDispatch, useSelector } from "react-redux"
import {
  selectInsightsEndDate,
  selectInsightsFilteredDays,
  selectInsightsNumberOfHits,
  selectInsightsSelectedReasons,
  selectInsightsSelectedStoreGroups,
  selectInsightsSelectedStoreMarkets,
  selectInsightsStartDate,
  selectInsightsTimeZone
} from "lib/store/services/insights/selectors"
import { selectSidebarCollapsed } from "lib/store/services/ui/selectors"
import {
  setEndDate,
  setFilteredDays,
  setNumberOfHits,
  setSelectedReasons,
  setSelectedStoreGroups,
  setSelectedStoreMarkets,
  setStartDate,
  setTimeZone
} from "lib/store/services/insights/slice"
import { Layout } from "./InsightsDashboard.styled"
import DataCard from "../../components/DashboardSales/DataCard"
import { Container } from "../../components/DashboardSales/DataCard.styled"
import dayjs from "dayjs"
import { useInsightsData } from "../../components/Insights/useInsightsData"
import { useChartData } from "../../components/Insights/useChartData"
import FreeTrialBadge from "../../components/Ui/Messages/FreeTrialBadge"
import PrimaryButton from "../../components/Ui/Buttons/PrimaryButton"
import { CartToOrderChart } from "../../components/Insights/CartToOrderChart"
import { CheckoutRateChart } from "../../components/Insights/CheckoutRateChart"
import { Common } from "lib/types"

interface AggregationBucket {
  key: string
  doc_count: number
  uniqueSessions: CardinalityAggregation
}

interface CardinalityAggregation {
  value: number
}

interface ChartDataPoint {
  name: string
  value: number
}

const NUMBER_OF_HITS_OPTIONS = ["25", "50", "100"]

const InsightsDashboard = () => {
  const { config } = useConfig()
  const dispatch = useDispatch()
  const [checkoutRateInterval, setCheckoutRateInterval] = useState<"hourly" | "daily">("hourly")
  const [cartToOrderInterval, setCartToOrderInterval] = useState<"hourly" | "daily">("daily")

  const filteredDays = useSelector(selectInsightsFilteredDays)
  const selectedStoreGroups = useSelector(selectInsightsSelectedStoreGroups)
  const selectedStoreMarkets = useSelector(selectInsightsSelectedStoreMarkets)
  const selectedReasons = useSelector(selectInsightsSelectedReasons)
  const startDate = useSelector(selectInsightsStartDate)
  const endDate = useSelector(selectInsightsEndDate)
  const numberOfHits = useSelector(selectInsightsNumberOfHits)
  const timeZone = useSelector(selectInsightsTimeZone)
  const sidebarCollapsed = useSelector(selectSidebarCollapsed)

  const {
    pendingCheckouts,
    aggregatedData,
    ordersData,
    totalCheckoutSessions,
    loading,
    error,
    handleRefresh
  } = useInsightsData({
    config,
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    selectedReasons,
    numberOfHits,
    timeZone
  })

  const {
    chartData: checkoutRateChartData,
    loading: checkoutRateLoading,
    error: checkoutRateError,
    refetch: checkoutRateRefetch
  } = useChartData({
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    interval: checkoutRateInterval,
    timeZone,
    enabled: config.insightsEnabled
  })

  const {
    chartData: cartToOrderChartData,
    loading: cartToOrderLoading,
    error: cartToOrderError,
    refetch: cartToOrderChartRefetch
  } = useChartData({
    startDate,
    endDate,
    selectedStoreGroups,
    selectedStoreMarkets,
    timeZone,
    interval: cartToOrderInterval,
    enabled: config.insightsEnabled
  })
  const { data: customStatesData } = useQuery(ALL_CUSTOM_STATES)
  const { data: storeGroupsData } = useQuery(ALL_STORE_GROUPS)

  const customStates: CustomState[] = customStatesData?.Order?.customStates?.states ?? []
  const storeGroups: StoreGroup[] = storeGroupsData?.getStoreGroups ?? []

  const customerOrders = useMemo(
    () => new CustomerOrders(customStates, storeGroups),
    [customStates, storeGroups]
  )

  const refresh = () => {
    // Update end date to "now" for rolling time ranges (Today, 7 days, 30 days)
    // But not for fixed historical ranges like "Yesterday" (filteredDays = 2)
    if (filteredDays > 0 && filteredDays !== 2) {
      const newEndDate = dayjs().format("YYYY-MM-DDTHH:mm")
      dispatch(setEndDate(newEndDate))
    }
    handleRefresh()
    checkoutRateRefetch()
    cartToOrderChartRefetch()
  }

  const paymentProviderData = useMemo((): ChartDataPoint[] => {
    const providerBuckets = aggregatedData?.paymentProviders?.buckets
    if (!providerBuckets) return []

    return providerBuckets
      .map((bucket: AggregationBucket) => ({
        name: getPaymentProviderDisplayName(bucket.key) || bucket.key,
        value: bucket.uniqueSessions.value
      }))
      .filter((item: ChartDataPoint) => item.value > 0)
      .sort((a: ChartDataPoint, b: ChartDataPoint) => b.value - a.value)
  }, [aggregatedData])

  const reasonData = useMemo((): ChartDataPoint[] => {
    if (!aggregatedData) return []

    const statusCounts = [
      { name: "Started", value: aggregatedData.startedSessions?.uniqueStartedSessions?.value ?? 0 },
      { name: "Pending", value: aggregatedData.pendingSessions?.uniquePendingSessions?.value ?? 0 },
      { name: "On Hold", value: aggregatedData.onHoldSessions?.uniqueOnHoldSessions?.value ?? 0 },
      {
        name: "Rejected",
        value: aggregatedData.rejectedSessions?.uniqueRejectedSessions?.value ?? 0
      }
    ]

    return statusCounts.filter((item) => item.value > 0).sort((a, b) => b.value - a.value)
  }, [aggregatedData])

  const storeGroupData = useMemo((): ChartDataPoint[] => {
    const storeGroupBuckets = aggregatedData?.storeGroups?.buckets
    if (!storeGroupBuckets) return []

    return storeGroupBuckets
      .map((bucket: AggregationBucket) => {
        const storeGroup = storeGroups.find((sg) => sg.id === bucket.key)
        return {
          name: storeGroup?.name || `Store Group ${bucket.key}`,
          value: bucket.uniqueSessions.value
        }
      })
      .filter((item: ChartDataPoint) => item.value > 0)
      .sort((a: ChartDataPoint, b: ChartDataPoint) => b.value - a.value)
  }, [aggregatedData, storeGroups])

  const storeMarketData = useMemo((): ChartDataPoint[] => {
    const countryBuckets = aggregatedData?.countryBreakdown?.buckets
    if (!countryBuckets) return []

    return countryBuckets
      .map((bucket: AggregationBucket) => ({
        name: getCountryName(bucket.key) || bucket.key,
        value: bucket.uniqueSessions.value
      }))
      .filter((item: ChartDataPoint) => item.value > 0)
      .sort((a: ChartDataPoint, b: ChartDataPoint) => b.value - a.value)
  }, [aggregatedData])

  // Calculate total checkouts without orders by subtracting sessions with orders
  const totalPendingCheckouts = totalCheckoutSessions - ordersData.total

  // Total unique checkout sessions (from separate query that includes ALL checkout sessions)
  const totalSessions = totalCheckoutSessions

  const conversionRate = useMemo(() => {
    if (totalCheckoutSessions === 0) return 0
    return Math.round((ordersData.total / totalCheckoutSessions) * 100)
  }, [ordersData.total, totalCheckoutSessions])

  // Calculate pending rate using unique sessions from aggregations
  const pendingRate = useMemo(() => {
    const uniquePendingSessions =
      aggregatedData?.pendingSessions?.uniquePendingSessions?.value ?? 0
    if (uniquePendingSessions === 0) return 0
    return Math.round((uniquePendingSessions / totalSessions) * 100)
  }, [aggregatedData, totalSessions])

  // Calculate rejection error rate using unique sessions
  const rejectionErrorRate = useMemo(() => {
    const uniqueRejectedSessions =
      aggregatedData?.rejectedSessions?.uniqueRejectedSessions?.value ?? 0
    if (uniqueRejectedSessions === 0) return 0
    return Math.round((uniqueRejectedSessions / totalSessions) * 100)
  }, [aggregatedData, totalSessions])

  // Calculate on hold error rate using unique sessions
  const onHoldErrorRate = useMemo(() => {
    const uniqueOnHoldSessions = aggregatedData?.onHoldSessions?.uniqueOnHoldSessions?.value ?? 0
    if (uniqueOnHoldSessions === 0) return 0
    return Math.round((uniqueOnHoldSessions / totalSessions) * 100)
  }, [aggregatedData, totalSessions])

  if (!config.insightsEnabled) {
    return (
      <>
        <PageHeader title={<>Insights Dashboard</>} />
      </>
    )
  }

  return (
    <>
      <PageHeader title={<>Insights Dashboard</>}>
        {" "}
        <FreeTrialBadge untilDate={"2026-02-28"} />
        <PrimaryButton
          handleClick={refresh}
          disabled={loading || checkoutRateLoading || cartToOrderLoading}
        >
          <RefreshIcon />
          Refresh
        </PrimaryButton>
      </PageHeader>
      <NoticeBanner type="info">
        This dashboard shows checkout sessions against PSPs that have not yet been added to Brink as
        actual orders. Use this to monitor payment processing issues and incomplete checkout flows.
      </NoticeBanner>

      <InsightsFilters
        storeGroups={storeGroups}
        selectedStoreGroups={selectedStoreGroups}
        selectedStoreMarkets={selectedStoreMarkets}
        selectedReasons={selectedReasons}
        startDate={startDate}
        endDate={endDate}
        loading={loading}
        numberOfHits={numberOfHits}
        numberOfHitsOptions={NUMBER_OF_HITS_OPTIONS}
        filteredDays={filteredDays}
        onStoreGroupsChange={(groups) => dispatch(setSelectedStoreGroups(groups))}
        onStoreMarketsChange={(markets) => dispatch(setSelectedStoreMarkets(markets))}
        onReasonsChange={(reasons) => dispatch(setSelectedReasons(reasons))}
        onDateRangeChange={(start, end) => {
          dispatch(setStartDate(start))
          dispatch(setEndDate(end))
        }}
        onNumberOfHitsChange={(hits) => dispatch(setNumberOfHits(hits))}
        onFilteredDaysChange={(days) => dispatch(setFilteredDays(days))}
        onTimeZoneChange={(timeZone: Common.Option) => dispatch(setTimeZone(timeZone.value))}
      />

      {error && <ErrorMessage message="Error when fetching checkouts" />}

      <Layout>
        <Container>
          <DataCard
            label={"Checkout Rate"}
            value={conversionRate}
            currency={"%"}
            infoText="Percentage of checkout sessions that successfully converted into completed orders"
            isInvertedComparison={false}
          />
          <DataCard
            label={"Pending Rate"}
            value={pendingRate}
            currency={"%"}
            infoText="Percentage of checkout sessions that are pending"
            isInvertedComparison={false}
          />
          <DataCard
            label={"Rejection Rate"}
            value={rejectionErrorRate}
            currency={"%"}
            infoText="Percentage of checkout sessions that were rejected by the payment provider"
            isInvertedComparison={true}
          />
          <DataCard
            label={"On Hold Rate"}
            value={onHoldErrorRate}
            currency={"%"}
            infoText="Percentage of checkout sessions that are currently on hold for manual review"
            isInvertedComparison={true}
          />
        </Container>
      </Layout>
      <CheckoutRateChart
        chartData={checkoutRateChartData}
        loading={checkoutRateLoading}
        error={checkoutRateError}
        interval={checkoutRateInterval}
        onIntervalChange={setCheckoutRateInterval}
        startDate={startDate}
        endDate={endDate}
      />
      <CartToOrderChart
        chartData={cartToOrderChartData}
        loading={cartToOrderLoading}
        error={cartToOrderError}
        interval={cartToOrderInterval}
        onIntervalChange={setCartToOrderInterval}
        startDate={startDate}
        endDate={endDate}
      />
      <Layout>
        <DashboardBarChart
          title="by Store Group"
          data={storeGroupData}
          totalCount={storeGroupData.reduce((sum, item) => sum + item.value, 0)}
        />{" "}
        <DashboardBarChart
          title="by Store Market"
          data={storeMarketData}
          totalCount={storeMarketData.reduce((sum, item) => sum + item.value, 0)}
        />
        <DashboardBarChart
          title="by PSP"
          data={paymentProviderData}
          totalCount={paymentProviderData.reduce((sum, item) => sum + item.value, 0)}
        />
        <DashboardBarChart
          title="by Reason"
          data={reasonData}
          totalCount={reasonData.reduce((sum, item) => sum + item.value, 0)}
        />
      </Layout>
      {loading && <div>Loading data...</div>}
      <InsightsCheckoutTable
        checkouts={pendingCheckouts}
        customerOrders={customerOrders}
        numberOfHits={numberOfHits}
        totalCount={totalPendingCheckouts}
        sidebarCollapsed={sidebarCollapsed}
      />
    </>
  )
}

export default InsightsDashboard
